import { Field, FieldArray, Formik } from 'formik';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import { Button, Select, SelectItem, Stack, TextInput } from '@carbon/react';
import type { OnboardingStatus } from 'src/context/types';
import { authClient } from 'src/services/auth/client';
import StepLayout from './StepLayout';
import { inviteSchema } from './schemas';

type InviteRole = 'admin' | 'member';

type InviteRow = { email: string; role: InviteRole };

type CreatedInvitation = { id: string; email: string; link: string };

const EMPTY_ROW: InviteRow = { email: '', role: 'member' };

const InviteStep = ({ status }: { status: OnboardingStatus }) => {
  const { t } = useTranslation('authentication');
  const navigate = useNavigate();
  const [invitations, setInvitations] = useState<CreatedInvitation[]>([]);
  const [error, setError] = useState<Error | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  async function onSubmit(values: { rows: InviteRow[] }) {
    const pending = values.rows.filter((row) => row.email.length > 0);
    if (pending.length === 0 || !status.organizationId) {
      return;
    }
    try {
      const created: CreatedInvitation[] = [];
      for (const row of pending) {
        const { data, error: inviteError } = await authClient.organization.inviteMember({
          email: row.email,
          role: row.role,
          organizationId: status.organizationId,
        });
        if (inviteError) throw new Error(inviteError.message);
        if (data) {
          created.push({
            id: data.id,
            email: data.email,
            link: `${window.location.origin}/accept-invitation/${data.id}`,
          });
        }
      }
      setInvitations((previous) => [...previous, ...created]);
    } catch (e) {
      setError(e as Error);
    }
  }

  async function onCopy(invitation: CreatedInvitation) {
    await navigator.clipboard.writeText(invitation.link);
    setCopiedId(invitation.id);
  }

  return (
    <Formik
      initialValues={{ rows: [EMPTY_ROW] }}
      validationSchema={toFormikValidationSchema(inviteSchema(t))}
      onSubmit={onSubmit}
    >
      {({ handleSubmit, isSubmitting, isValid, values }) => (
        <StepLayout
          title={t('onboarding.invite.title')}
          subtitle={t('onboarding.invite.subtitle')}
          help={t('onboarding.invite.help')}
          error={error}
          onSubmit={handleSubmit}
          actions={
            <>
              <Button
                type="submit"
                disabled={isSubmitting || !isValid || values.rows.every((row) => row.email.length === 0)}
              >
                {t('onboarding.invite.send')}
              </Button>
              {/* The wizard is optional from here — leaving without inviting is valid. */}
              <Button kind="ghost" type="button" onClick={() => navigate('/')}>
                {t('onboarding.invite.finish')}
              </Button>
            </>
          }
        >
          <Stack gap={5}>
            <FieldArray name="rows">
              {({ push }) => (
                <>
                  {values.rows.map((_, index) => (
                    // Rows are only ever appended, never reordered or removed.
                    <div className="onboarding-invite__row" key={index}>
                      <Field name={`rows.${index}.email`}>
                        {({ field, meta }) => (
                          <TextInput
                            id={`invite-email-${index}`}
                            labelText={t('onboarding.invite.email')}
                            type="email"
                            invalid={!!(meta.touched && meta.error)}
                            invalidText={meta.error}
                            {...field}
                          />
                        )}
                      </Field>
                      <Field name={`rows.${index}.role`}>
                        {({ field }) => (
                          <Select id={`invite-role-${index}`} labelText={t('onboarding.invite.role')} {...field}>
                            <SelectItem value="admin" text={t('onboarding.invite.roleAdmin')} />
                            <SelectItem value="member" text={t('onboarding.invite.roleMember')} />
                          </Select>
                        )}
                      </Field>
                    </div>
                  ))}
                  <div className="onboarding-invite__controls">
                    <Button kind="ghost" size="sm" type="button" onClick={() => push({ ...EMPTY_ROW })}>
                      {t('onboarding.invite.add')}
                    </Button>
                  </div>
                </>
              )}
            </FieldArray>

            {invitations.length > 0 && (
              <ul className="onboarding-invite__sent">
                {invitations.map((invitation) => (
                  <li key={invitation.id}>
                    <span className="onboarding-invite__sent-email">{invitation.email}</span>
                    <Button kind="ghost" type="button" size="sm" onClick={() => onCopy(invitation)}>
                      {copiedId === invitation.id ? t('onboarding.invite.copied') : t('onboarding.invite.copyLink')}
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </Stack>
        </StepLayout>
      )}
    </Formik>
  );
};

export default InviteStep;
